// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification
} from 'firebase/auth';
import { auth } from '../services/firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userClaims, setUserClaims] = useState(null);

  // Sign up function
  async function signup(email, password, userData) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Update user profile with additional data
    await updateProfile(userCredential.user, {
      displayName: userData.name
    });

    // Send email verification
    await sendEmailVerification(userCredential.user);

    return userCredential;
  }

  // Login function
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // Logout function
  function logout() {
    return signOut(auth);
  }

  // Reset password function
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // Refresh token to get latest claims
  async function refreshToken() {
    if (currentUser) {
      await currentUser.getIdToken(true); // Force refresh
      await checkAdminStatus(currentUser);
    }
  }

  // Check if user has admin claims
  const checkAdminStatus = async (user) => {
    if (user) {
      try {
        const idTokenResult = await user.getIdTokenResult();
        const hasAdminClaim = !!idTokenResult.claims.admin;
        const isEmailAdmin = user.email === 'flexlogisticsmanagement1@gmail.com';
        
        console.log('AuthContext - Admin check:', {
          email: user.email,
          hasAdminClaim,
          isEmailAdmin,
          claims: idTokenResult.claims
        });
        
        const adminStatus = hasAdminClaim || isEmailAdmin;
        setIsAdmin(adminStatus);
        setUserClaims(idTokenResult.claims);

        // Log for debugging
        if (adminStatus) {
          console.log('✅ User is ADMIN - will redirect to admin dashboard');
        } else {
          console.log('👤 User is REGULAR USER - will redirect to user dashboard');
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
        setUserClaims(null);
      }
    } else {
      setIsAdmin(false);
      setUserClaims(null);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      await checkAdminStatus(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    isAdmin,
    userClaims,
    signup,
    login,
    logout,
    resetPassword,
    refreshToken,
    checkAdminStatus: () => checkAdminStatus(currentUser)
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}